import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useMemo, useState } from 'react';
import { useContactSetKeepInTouchMutation } from '../../../../../../../queries/Contact';
import { useContactDeleteKeepInTouchMutation } from '../../../../../../../queries/Contact/useContactDeleteKeepInTouchMutation';
import { useContactKeepInTouchQuery } from '../../../../../../../queries/Contact/useContactKeepInTouchQuery';
import { Button } from '../../../../../../components/Button';
import { Dropdown } from '../../../../../../components/Dropdown';
import { ClockIcon } from '../../../../../../components/svgs/icons/ClockIcon';
import { styleSheet } from '../styles';

type Props = {
	contact: Api.IContact;
};

const KitOptions: [string, number][] = [
	['Every month', 1],
	['Every 2 months', 2],
	['Every 3 months', 3],
	['Every 6 months', 6],
	['Every year', 12],
];

export const KeepInTouch = ({ contact }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [keepInTouchFrequency, setKeepInTouchFrequency] = useState(-1);

	const {
		data: keepInTouchQuery,
		refetch: refetchKeepInTouchQuery,
		isError: isKeepInTouchError,
	} = useContactKeepInTouchQuery({
		contactId: contact.id,
		enabled: Boolean(contact.id),
	});

	const setKeepInTouchMutation = useContactSetKeepInTouchMutation({
		onSuccess: () => {
			setIsOpen(false);
			refetchKeepInTouchQuery();
		},
		onError: () => {
			setIsOpen(false);
		},
	});

	const deleteKeepInTouchMutation = useContactDeleteKeepInTouchMutation({
		onSuccess: () => {
			setIsOpen(false);
			refetchKeepInTouchQuery();
		},
		onError: () => {
			setIsOpen(false);
		},
	});

	useEffect(() => {
		if (keepInTouchQuery) {
			setKeepInTouchFrequency(keepInTouchQuery.frequency);
		}
	}, [keepInTouchQuery]);

	const hasKeepInTouch = useMemo(() => {
		return keepInTouchQuery?.frequency > 0 && !isKeepInTouchError;
	}, [keepInTouchQuery, isKeepInTouchError]);

	return (
		<div>
			<Dropdown
				anchor={
					hasKeepInTouch ? (
						<div className={css(styleSheet.keepInTouchInfo)}>
							<ClockIcon />
							<div className={css(styleSheet.keepInTouchText)}>
								<span>{`Keep in touch ${KitOptions.find(x => x[1] === keepInTouchFrequency)?.[0]}`}</span>
							</div>
							<Button kind='link' onClick={() => setIsOpen(true)} className={css(styleSheet.keepInTouchChangeBtn)}>
								Change
							</Button>
						</div>
					) : (
						<div className={css(styleSheet.keepInTouchInfo)}>
							<ClockIcon />

							<Button kind='link' onClick={() => setIsOpen(true)}>
								+ Add Keep in Touch Reminder
							</Button>
						</div>
					)
				}
				contentPositionX={keepInTouchFrequency > 0 ? 'right' : 'left'}
				isOpen={isOpen}
				onOpenChanged={setIsOpen}
			>
				<ul>
					{KitOptions.map(option => {
						return (
							<li
								className={css(styleSheet.keepInTouchOption)}
								key={option[1]}
								onClick={() => {
									if (setKeepInTouchMutation.isPending) {
										return;
									}

									setKeepInTouchMutation.mutate([
										{
											contactId: contact.id,
											frequency: option[1],
										},
									]);
								}}
							>
								{option[0]}
							</li>
						);
					})}
					{hasKeepInTouch && (
						<li
							className={css(styleSheet.keepInTouchOption)}
							key='remove'
							onClick={() => {
								if (deleteKeepInTouchMutation.isPending || !keepInTouchQuery?.id) {
									return;
								}

								deleteKeepInTouchMutation.mutate(keepInTouchQuery.id);
							}}
						>
							No schedule
						</li>
					)}
				</ul>
			</Dropdown>
		</div>
	);
};
